import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, NumberInput, Toggle } from '@carbon/react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError } from '../components/states';
import { When } from '../components/format';
import { CommandStatus } from '../components/labels';
import type { Command } from './Commands';

// Bot no hu va bot gop hu chung cho moi game (config `dieu_khien` global.hu, backend/lib/bot_no_hu.js):
// tab "Bot hũ" cua trang Hu (quyen jackpots.manage) va khoi "Luật hũ và thông báo" cua trang "Quỹ và bot"
// dung chung cac o nay.
// gioToiThieu / gioToiDa: hours between two bot hits of one pot (absent from a he A build older than them)
export type BotNo = { bat: boolean; tyLe: number; heSoNguong: number; giayKiem: number; gioToiThieu?: number; gioToiDa?: number };
export type BotGop = { bat: boolean; phanTramMoiPhut: number; heSoTran: number };

export const pct = (x: number) => Math.round(x * 1000) / 10;
export const num = (v: number | string) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
export const clamp = (v: number | string, a: number, b: number) => Math.min(b, Math.max(a, num(v)));
export const secs = (v: number | string) => Math.floor(clamp(v, 5, 86400));
export const hours = (v: number | string) => Math.round(clamp(v, 0, 720) * 10) / 10;

/** Bot jackpot hit and growth inputs; ids are `<id>-bn`, `<id>-tl`, `<id>-gk`, `<id>-gmin`, `<id>-gmax`, `<id>-hs`, `<id>-bg`, `<id>-bgp`, `<id>-bgt`. */
export function BotHuFields({ id, botNo, botGop, onBotNo, onBotGop }: {
  id: string; botNo: BotNo; botGop?: BotGop | null; onBotNo: (p: Partial<BotNo>) => void; onBotGop: (p: Partial<BotGop>) => void;
}) {
  return (
    <>
      <Toggle id={id + '-bn'} size="sm" labelText="Bot nổ hũ" labelA="Tắt" labelB="Bật" toggled={botNo.bat} onToggle={(v) => onBotNo({ bat: v })} />
      <div className="row">
        <NumberInput id={id + '-tl'} size="sm" label="Xác suất bot nổ mỗi lần kiểm (%)" min={0} max={100} step={0.1} value={pct(botNo.tyLe)}
          onChange={(_e, { value }) => onBotNo({ tyLe: Math.round(clamp(value, 0, 100) * 10) / 1000 })} />
        <NumberInput id={id + '-gk'} size="sm" label="Kiểm mỗi (giây)" min={5} max={86400} step={5} value={botNo.giayKiem}
          onChange={(_e, { value }) => onBotNo({ giayKiem: secs(value) })} />
      </div>
      <div className="row">
        <NumberInput id={id + '-gmin'} size="sm" label="Mỗi hũ: bot nổ lại sớm nhất sau (giờ)" min={0} max={720} step={1} value={botNo.gioToiThieu ?? 0}
          onChange={(_e, { value }) => onBotNo({ gioToiThieu: hours(value) })} />
        <NumberInput id={id + '-gmax'} size="sm" label="Mỗi hũ: bot nổ chắc chắn sau (giờ)" min={0} max={720} step={1} value={botNo.gioToiDa ?? 0}
          onChange={(_e, { value }) => onBotNo({ gioToiDa: hours(value) })} />
      </div>
      <p className="muted note">Mỗi hũ, tính từ lần bot nổ trước: chưa tới giờ sớm nhất thì không nổ; từ đó tới giờ chắc chắn nổ theo xác suất mỗi lần kiểm; quá giờ chắc chắn thì bot nổ ngay (hũ phải trên tiền khởi tạo). 0 = bỏ mốc đó.</p>
      <NumberInput id={id + '-hs'} size="sm" label="Bot chỉ nổ khi hũ ≥ tiền khởi tạo × hệ số" min={1} max={100} step={0.1} value={botNo.heSoNguong}
        onChange={(_e, { value }) => onBotNo({ heSoNguong: clamp(value, 1, 100) })} />
      <p className="muted note">Bot nổ hũ đưa hũ về tiền khởi tạo, không cộng ví nào và không đổi quỹ.</p>
      {botGop ? (
        <>
          <Toggle id={id + '-bg'} size="sm" labelText="Bot góp hũ (hũ tự tăng)" labelA="Tắt" labelB="Bật" toggled={botGop.bat} onToggle={(v) => onBotGop({ bat: v })} />
          <div className="row">
            <NumberInput id={id + '-bgp'} size="sm" label="Hũ tăng mỗi phút (% tiền khởi tạo)" min={0} max={10} step={0.05} value={botGop.phanTramMoiPhut}
              onChange={(_e, { value }) => onBotGop({ phanTramMoiPhut: Math.round(clamp(value, 0, 10) * 1000) / 1000 })} />
            <NumberInput id={id + '-bgt'} size="sm" label="Hũ tăng tối đa tới tiền khởi tạo ×" min={1} max={100} step={0.5} value={botGop.heSoTran}
              onChange={(_e, { value }) => onBotGop({ heSoTran: clamp(value, 1, 100) })} />
          </div>
          <p className="muted note">Bot góp chỉ tăng số hũ, không vào ví, quỹ hay sổ giao dịch. 0,1 % mỗi phút = 6 % tiền khởi tạo mỗi giờ.</p>
        </>
      ) : null}
    </>
  );
}

type Data = {
  hu: { botNo: BotNo; botGop: BotGop | null } | null; overridden: boolean;
  meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string; createdAt: string }[];
};
type Form = { botNo: BotNo; botGop: BotGop };

function Editor({ x, version, editable, pending }: { x: Form; version: number; editable: boolean; pending: boolean }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [f, setF] = useState<Form>(x);
  const [open, setOpen] = useState(false);
  const [stale, setStale] = useState(false);
  const dirty = JSON.stringify(f) !== JSON.stringify(x);
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/jackpots/bot', { ...f, expect: version }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && e.code === 'STALE') { setOpen(false); setStale(true); qc.invalidateQueries({ queryKey: ['hu-bot'] }); }
    },
  });
  return (
    <section className="panel control">
      {stale ? <InlineNotification kind="warning" lowContrast title="Cấu hình vừa được người khác lưu"
        subtitle="Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại." onClose={() => setStale(false)} /> : null}
      <fieldset className="control__group" disabled={!editable}>
        <legend>Bot nổ hũ và bot góp hũ (mọi game)</legend>
        <BotHuFields id="jb" botNo={f.botNo} botGop={f.botGop}
          onBotNo={(p) => setF((o) => ({ ...o, botNo: { ...o.botNo, ...p } }))}
          onBotGop={(p) => setF((o) => ({ ...o, botGop: { ...o.botGop, ...p } }))} />
      </fieldset>
      {editable ? (
        <div className="row control__actions">
          <Button kind="ghost" size="sm" disabled={!dirty} onClick={() => setF(x)}>Bỏ thay đổi</Button>
          <Button kind="primary" size="sm" disabled={!dirty || pending} onClick={() => setOpen(true)}>Lưu, áp dụng ngay</Button>
        </div>
      ) : null}
      <Modal open={open} modalHeading="Đổi bot nổ hũ và bot góp hũ" primaryButtonText="Áp dụng ngay" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <p>Áp dụng cho hũ của mọi game. Bot chỉ đổi số hũ, không đổi ví, quỹ hay sổ giao dịch. Máy chủ áp dụng ngay, không chờ người duyệt.</p>
          <InlineError error={m.error} />
        </div>
      </Modal>
    </section>
  );
}

// Tab "Bot hu" cua trang Hu
export function JackpotBot() {
  const { can } = useAuth();
  const q = useQuery({ queryKey: ['hu-bot'], queryFn: () => api.get<Data>('/api/admin/jackpots/bot') });
  if (q.isLoading) return <BlockSkeleton lines={8} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { hu, meta, pending, overridden } = q.data;
  return (
    <div className="stack">
      <p className="muted">Bot nổ hũ và bot góp hũ chung cho mọi game. Tiền khởi tạo (mốc) của từng hũ đổi ở tab Hũ đang chạy, phần cược góp vào hũ ở tab Luật hũ. Người thật tự nổ hũ đặt ở trang Quỹ và bot.</p>
      <div className="row health">
        <span>Cấu hình CMS: {meta.source === 'default' || !overridden ? <span className="muted">chưa đặt, máy chủ dùng mặc định</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
      </div>
      {pending.length ? (
        <>
          <InlineNotification kind="info" lowContrast hideCloseButton title={`${pending.length} lệnh đang chờ`}
            subtitle="Đợi lệnh đang gửi xong, hoặc duyệt / từ chối lệnh còn chờ duyệt từ trước, rồi mới tạo lệnh mới: lệnh tạo trên phiên bản cũ sẽ bị hệ A từ chối." />
          <ul className="list pending-list">
            {pending.map((p) => (
              <li key={p.id}><CommandStatus s={p.status} /><Link className="cell-link" to={'/commands/' + p.id}>Xem lệnh</Link><span className="muted"><When v={p.createdAt} /></span></li>
            ))}
          </ul>
        </>
      ) : null}
      {hu?.botGop ? (
        <div className="control-grid">
          <Editor key={meta.version} x={{ botNo: hu.botNo, botGop: hu.botGop }} version={meta.version} editable={can('jackpots.manage')} pending={pending.length > 0} />
        </div>
      ) : <ErrorState error={new Error('Hệ A chưa trả luật bot hũ. Thử lại sau.')} onRetry={() => q.refetch()} />}
    </div>
  );
}
